import { Component, OnInit } from '@angular/core';

interface FaqItem {
  question: string;
  answer: string;
  videoLinks?: { label: string; url: string }[];
}

interface FaqCategory {
  icon: string;
  name: string;
  items: FaqItem[];
}

@Component({
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.css']
})
export class FaqComponent implements OnInit {
  categories: FaqCategory[] = [
    {
      icon: '📋',
      name: 'Lessons & Posting',
      items: [
        {
          question: 'How do I post a lesson?',
          answer: 'If you want to post the lessons one at a time, you can use the orange "Post Lesson" button from the student pop up. Or, you can post multiple lessons by using the blue "Edit Status" button on the top right of the Day View. Simply mark the checkbox(es) you want to post and change the status on the bottom dropdown to "showed". Then hit save.',
          videoLinks: [
            { label: 'Post Lessons Tutorial', url: 'https://drive.google.com/file/d/16E6UBR5BFQEMvGieEYjZPr6WHg9fJIKU/view?usp=drive_link' }
          ]
        },
        {
          question: 'What if I run into an error message when I post a lesson?',
          answer: 'All lessons must be attached to an enrollment and only paid lessons will post. Sometimes that enrollment has run out, sometimes that student needs to make a payment, and some lessons may have migrated over from your other program with no enrollment.\n\nIf your error message says "Something Went Wrong" and either "No enrollment attached to this event" or "Student has already taken all enrolled lessons (25/25)", simply open the lesson pop up and click edit lesson. Go to the pencil by the student name and Add/Change the enrollment.\n\nIf "Lessons with attendees with 0 available lessons paid ahead can\'t be posted" comes up on your screen, simply make a payment to the account and then you can post the lessons.',
          videoLinks: [
            { label: 'No Enrollment Fix', url: 'https://drive.google.com/file/d/1gBuNcziqMDpbGCDghYyJABwFpGE5kGr1/view?usp=drive_link' },
            { label: 'Taken All Lessons Fix', url: 'https://drive.google.com/file/d/1FPx9kYDz2oP8vwHGPi_R0g90CAmkEfvQ/view?usp=drive_link' }
          ]
        },
        {
          question: 'My student lesson is locked and I need to make changes to it. What do I do?',
          answer: 'Edit the status (top of the student pop up) to pending and then edit the enrollment (edit lesson/pencil by the student name). Now, you can add notes, change enrollments, or even move the lesson.',
          videoLinks: [
            { label: 'Unlock Lesson Tutorial', url: 'https://drive.google.com/file/d/1Gd3lrNodW_KejztSFl22F69nNT_AglYo/view?usp=drive_link' }
          ]
        },
        {
          question: 'How do I fill out and send virtual lesson blocks?',
          answer: 'Open up the student pop up and click the "Lesson Blocks" tab on the top. Fill in the Material Covered on THIS lesson and use the Comments as your Sneak Preview for the NEXT. Let the student give you 1-5 stars and get both the teacher\'s initials and the student\'s. You can "save and exit" or "save and email". The Lesson block can be planned and filled in before the lesson. This saves time and shows that you are prepared to teach. The lesson block is saved in two places: on the lesson itself AND on the "Lesson Blocks" tab on the student dashboard. The latter is printable by date for the student\'s binder.',
          videoLinks: [
            { label: 'Lesson Blocks Tutorial', url: 'https://drive.google.com/file/d/1XZgNBdzjdu4FNa_ILZH-65G2C6G6pcOP/view?usp=drive_link' }
          ]
        },
        {
          question: 'How do I book a Coaching Lesson?',
          answer: 'Coaching sessions are enrolled and booked as Non Unit (non unit private). You\'ll need to first create a Coaching Package (coaching is a non unit SPS code, Non Unit Private lesson type and Partytime/Non unit category). Second, create an enrollment for the student. Then, when you book it, you\'ll put it in as a "lesson", but will need to change the lesson type to Non Unit Private. Then add the enrollment from the dropdown. It will show you the Non Unit enrollments available on that student account. Once all of that is done, you can post it like normal and it\'ll come off the Coaching counts.'
        }
      ]
    },
    {
      icon: '📅',
      name: 'Scheduling & Appointments',
      items: [
        {
          question: 'How do I cancel and/or Reschedule appointments?',
          answer: 'Click on the student appointment to bring up the pop up. Use the gray "Cancel/RS" button.\n\nTo CANCEL - skip the date and put in the reason the student is cancelling and save. The lesson will gray out and the note will show up on the schedule.\n\nTo RESCHEDULE - Use the calendar icon to input the date that the student wants to change to. You can also put in a reason for the teacher to see. Click save. The lesson will again gray out with the new date and note, and the new lesson will appear on the date you put in.',
          videoLinks: [
            { label: 'Cancel/Reschedule Tutorial', url: 'https://drive.google.com/file/d/1SJbQYGiDfZlCIPI6s2VbrJq0taYaw4z3/view?usp=drive_link' }
          ]
        },
        {
          question: 'How do I charge a student that Cancelled Too Late or Did Not Show?',
          answer: 'If you want to charge the student for missing their lesson, you can open the pop up and use the "Edit Status" tab on top. You can charge them using Cancelled Too Late or No Show Charged. The lesson will become darker gray with CTL and a golden brown with a NS Charge. These lessons will be deducted from the student account, will be added to the lesson counts of the studio overall and the lesson counts of the teacher.',
          videoLinks: [
            { label: 'CTL/No Show Charge Tutorial', url: 'https://drive.google.com/file/d/1Ww0Tl-yzxC4ec4Da5zV-9TTLgFvKRxhK/view?usp=drive_link' }
          ]
        },
        {
          question: 'Can I schedule Meetings and Dance Sessions on Multiple teachers?',
          answer: 'Yes! Click the desired date and time in the Day View. Choose "Schedule". Choose the type of schedule (meeting, meal break, dance session) and open the instructor drop down. You can choose multiple instructors for this event. Click save and the event will schedule for that day on multiple teachers.\n\nIf you\'d like the event to repeat open the standing event section, Choose daily, weekly, monthly and the day(s) you want them to repeat. You MUST put an end date on every standing appointment. (Please don\'t go out 5 years, as it will slow your system down.) Hit save and the repeating sessions will populate.'
        }
      ]
    },
    {
      icon: '💰',
      name: 'Sales & Tracking',
      items: [
        {
          question: 'How do I track my Sales and No Sales?',
          answer: 'Your sales are tracked through the Service "Chats" on the Day View. The Reports track Tried and Sold through the DE/Pre Chats, ORI Chats, EXT Chats and REN Chats. (These are the Closes your Counselor/Supervisor do.) Simply add the chat to the Executive or Teacher columns. Use the "Edit Status" tab on the top of the lesson pop up. Mark it as Sale, No Sale, Think it over, etc. Click save. These will show up as your T and S on the AMI Business Report.',
          videoLinks: [
            { label: 'Sales Tracking Tutorial', url: 'https://drive.google.com/file/d/1jFv6syaRYteQXky7eCXw4fxG-Kta1nM6/view?usp=drive_link' }
          ]
        },
        {
          question: 'How do I process a Drop or Refund?',
          answer: 'Either from the Enrollment List on the Student Dashboard, or View Enrollments on the main menu bar, click on "Drop" in the action button. You fill in the Drop date and verify the amount. Your options become Drop, and Drop and Refund.\n\nIf they simply want their money back and will pay later, DROP. If they want to quit altogether and get their money back, DROP AND REFUND.\n\nSince we can\'t legally change the enrollment, the system edits the display, removes the lessons and money from the account and puts the refund on the reports. You can do this for lessons and miscellaneous. The refund portion will show up on the royalty reports. You only have to send "proof" of the refunded check or CC slip to Elena to receive credit.',
          videoLinks: [
            { label: 'Lesson Drop Tutorial', url: 'https://drive.google.com/file/d/12ogNrys2HvSPY4H5FwkeId9GzGsL5Ptx/view?usp=drive_link' },
            { label: 'Misc Drop Tutorial', url: 'https://drive.google.com/file/d/1siJ5fy7fVbAr_1oFJ6vjp9HYBw10wj3e/view?usp=drive_link' }
          ]
        }
      ]
    },
    {
      icon: '📊',
      name: 'Reports',
      items: [
        {
          question: 'How do I send a Miscellaneous Service Report for DOR?',
          answer: 'Go to Reports, AMI, Misc Reports. You\'ll choose the event, put in the receipt dates you\'re looking for and the event dates. The submit button won\'t open if you\'re trying to create this week (choose last week). You\'ll be able to put in the deduction amounts for the AM invoice (subtotal of packages) and the travel receipts.',
          videoLinks: [
            { label: 'Misc Report Video', url: 'https://drive.google.com/file/d/1CMes1NINyBgHHKTeNOmbwtFlb5ye-Ayr/view?usp=drive_link' }
          ]
        },
        {
          question: 'How do I send Reports to AMI?',
          answer: 'The reports are calculated on a cumulative basis, and refresh instantly as you put in/post new info. Go to the reports section in the main menu bar. Click AMI. Choose which report you want to view, open the filter bar and choose the week #. Check the number to see that everything has been put in and click "send report". (If it\'s grayed out, it\'s too early to send.) Use the links on top to switch between the same week\'s reports. Open, check and send the others. Below the tiles you can see which reports have been sent to online reporting. The dark blue icons mean they have been submitted and accepted.',
          videoLinks: [
            { label: 'Send Reports Tutorial', url: 'https://drive.google.com/file/d/1T_2Y0icu21mFWe6L8Q9VxMICyTc58T_p/view?usp=drive_link' }
          ]
        }
      ]
    },
    {
      icon: '📱',
      name: 'Reminders & Notifications',
      items: [
        {
          question: 'How do I set up our automated text/email lesson reminders?',
          answer: 'You just have to turn the reminders on and make sure that each student has the "opt in" confirmation box checked in their student account profile.\n\nGo to your username on the right side of the screen, then click configurations. Scroll down and see the SMS and EMAIL configurations. Click one of them and slide the blue slider to open and choose which ONE of the reminder times you want: 24, 48, or 72 hours. You can change the wording if you want. Then choose the other configuration option and do the same. Most people are choosing a 48 hour email and a 24 hour text. Make sure you save each one.'
        },
        {
          question: 'Why is my student telling me they\'re not getting our text/email lesson reminders?',
          answer: 'This could be due to a couple of things. Please check their email address and/or phone number. Is it their cell? And is their email REAL? There is also a consent given box in their Student Account information. You can also choose if they\'d prefer Text, Email or Both. If they\'d like to opt out, simply uncheck the consent box. This box also needs to be checked for them to receive an email of the lesson block, a digital copy of the receipt and if they want a digital copy of their enrollment.',
          videoLinks: [
            { label: 'Reminder Troubleshooting', url: 'https://drive.google.com/file/d/1yKegnts9W5-Reiqug1RPRVgXul-ipEPb/view?usp=drive_link' }
          ]
        }
      ]
    },
    {
      icon: '👥',
      name: 'Students & Accounts',
      items: [
        {
          question: 'Can I add a second person to my student\'s account?',
          answer: 'Of Course! Go to their inquiry info and click "Add Student". Then you can put in a second person\'s information.'
        },
        {
          question: 'How do we handle Teaching Time Exchange?',
          answer: 'Until we get our TTE module up and running between schools, do the following:\n\nIf they\'re from another school and taking the lessons in your studio:\n\n1. Create an account in Add Student account (the dropdown from "Students" on the dark blue menu bar). Adding them this way, they won\'t be marked as an inquiry. (You can use Add inquiry, but don\'t book the lesson, if you DO want it to be an inquiry.)\n\n2. Create a TTE package (I have TTE front and TTE back). Use Bonus lesson type and either EXT or REN SPS codes. Make it a $0 for 1 lesson package.\n\n3. Enroll them "x" amount of lessons based on how many they\'re transferring. That way you can book and post them.\n\n4. Mark the lessons with a note on the day view (TTE - from school).\n\n5. Now you can Book and post them like normal.\n\n6. Let the other school know you\'ve taught them using the TTE form from the FTP server.\n\nAMI pays you the TTE money onto your online reporting account. It doesn\'t NEED to go through the student account, but if you want, you can adjust the price later when that money shows up on your account.\n\nOur TTE module will be much easier and I\'m hoping to have it completed before the end of the year.'
        },
        {
          question: 'How do I add a student account who was INACTIVE when I migrated over to Agenda Master?',
          answer: 'Go to your Username on the top right and click configuration from the dropdown. On the left side of the screen, scroll down to Simple import. Click on it then choose spreadsheet.\n\nStep 1: "Download" the template to your download file. Open it and fill it in how you did for your original spreadsheet. (See Migration walkthrough if you need it). Save the file.\n\nStep 2: Click on "select file". Then upload the spreadsheet. In a couple of seconds the account is created and you can view the dashboard from this page.'
        }
      ]
    },
    {
      icon: '🔧',
      name: 'Troubleshooting',
      items: [
        {
          question: 'How do I "Clear my Cache"?',
          answer: 'Sometimes the systems need you to do a hard refresh. We may have done a big update, or something simply glitched and Agenda Master isn\'t displaying properly with your computer/internet. A quick fix is to clear your cache.\n\nFor Windows Users: Hold Ctrl and press F5.\n\nFor MAC Users: Hold down Cmd and Shift then press R.\n\nFor iPad/Phone users: Pull down on your screen to refresh.'
        }
      ]
    }
  ];

  selectedCategory: FaqCategory;
  expandedItems: Set<number> = new Set();

  constructor() {
    this.selectedCategory = this.categories[0];
  }

  ngOnInit(): void {}

  selectCategory(category: FaqCategory): void {
    this.selectedCategory = category;
    this.expandedItems.clear();
  }

  toggleItem(index: number): void {
    if (this.expandedItems.has(index)) {
      this.expandedItems.delete(index);
    } else {
      this.expandedItems.add(index);
    }
  }

  isExpanded(index: number): boolean {
    return this.expandedItems.has(index);
  }

  openKnowledgeBase(): void {
    window.open('https://docs.arthurmurray.com/', '_blank', 'noopener,noreferrer');
  }

  openFaqDoc(): void {
    window.open(
      'https://docs.google.com/document/d/1N4iO0KYN0Hm0yGk7iXigO18TKU_oBBi-XtxsLo9Abxg/edit?tab=t.0#heading=h.gvq7n2atmxkt',
      '_blank',
      'noopener,noreferrer'
    );
  }
}

